import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../../base/form";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import {Col, Row} from "reactstrap";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import {OutlinedDropdownForm} from "../../../../../base/dropdown/outlined_dropdown";
import RGAInput from "../../../../../base/input/rga-input";
import {CheckboxForm} from "../../../../../base/checkbox/checkbox";
import {Divider} from "@mui/material";
import Button from "../../../../../base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {EditPartSupportRequestProcessSteps} from "../../index";

const schema = Yup.object().shape({
    autoRga: Yup.boolean().default(false).nullable(),
    inventoryBased: Yup.boolean().default(false).nullable(),
    rga: Yup.string().trim().when('autoRga', {
        is: (val) => !!val,
        then: Yup.string().nullable(),
        otherwise: Yup.string().nullable().required(ValidateMessages.required),
    }),
    type: Yup.mixed().nullable().required(ValidateMessages.required),
    customer: Yup.mixed().when('warehouse', {
        is: val => !!val,
        then: Yup.mixed().nullable(),
        otherwise: Yup.mixed().nullable().required(ValidateMessages.required)
    }),
    warehouse: Yup.mixed().when('customer', {
        is: val => !!val,
        then: Yup.mixed().nullable(),
        otherwise: Yup.mixed().nullable().required(ValidateMessages.required)
    }),
}, [['customer', 'warehouse'], ['warehouse', 'customer']]);


const EditPartSupportRequestBasicInfo = ({
                                             basicInfo,
                                             setBasicInfo,
                                             initialBasicInfo,
                                             setProcess,
                                             types,
                                             loadingTypes,
                                             setOpen,
                                             warehouses,
                                         }) => {
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const [validatingRga, setValidatingRga] = useState(false)
    const rgaInput = useRef();
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();


    /**
     * Listens for the changes in initialBasicInfo and with each change:
     * - sets the initial values of the form.
     */
    useLayoutEffect(() => {
        rgaInput.current?.resetValidation();
    }, [initialBasicInfo])


    useEffect(() => {
        if (!formRef.current)
            return;
        for (const [key, value] of Object.entries(basicInfo)) {
            formRef.current.change(key, value);
        }
        rgaInput.current?.resetValidation();
    }, [basicInfo])


    /**
     * Sets the basic info values with the current form values and then proceeds to the product info section of the
     * dialog.
     */
    const onSubmit = (values) => {
        setBasicInfo({...values});
        setProcess(EditPartSupportRequestProcessSteps.productInfo);
    }

    /**
     * If checked sets the autoRga to true and removes the rga from values of form, otherwise substitutes the rga
     * with its initial value.
     * @param e
     * @param form
     */
    const onAutoRgaChanged = (e, form) => {
        if (e.target.checked) {
            form.change('rga', null);
            form.resetFieldState('rga');
        } else {
            form.change('rga', initialBasicInfo.rga)
        }
        rgaInput.current?.resetValidation();
        form.change(e.target.name, e.target.checked)
        setTimeout(() => {
            if (!isMounted())
                return;
            form.resetFieldState('rga');
            rgaInput.current?.resetValidation();
        }, 100)
    }

    return (
        <div className=''>
            <Form
                className='w-100'
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialBasicInfo}
                render={({values, initialValues, form}) => {
                    formRef.current = form;
                    const haveChanges = !deepEqual({
                            ...values,
                            customer: undefined,
                            warehouse: undefined,
                            customerId: values.customer?.id,
                            warehouseCode: values?.warehouse?.code,
                        },
                        {
                            ...initialValues,
                            customer: undefined,
                            warehouse: undefined,
                            customerId: initialValues.customer?.id,
                            warehouseCode: initialValues?.warehouse?.code,
                        });
                    const submitButtonDisabled = validatingRga ||
                        (!values.customer && !values.inventoryBased) ||
                        (!values.warehouse && values.inventoryBased) ||
                        (!values.autoRga && !values.rga?.length);

                    return (
                        <Row className='no-gutters justify-content-center align-items-start w-100'>
                            {
                                !values?.inventoryBased &&
                                <Col xs={6} className={'h-100 pl-4'}>
                                    <p className={'text-md font-weight-semi-bold mb-2'}>
                                        Current Customer
                                    </p>
                                    <div className={'edit-psq-card'}>
                                        <p>
                                            Customer: <span>{values?.customer?.name ?? '--'}</span>
                                        </p>
                                    </div>
                                </Col>
                            }
                            <Col
                                xs={values?.inventoryBased ? 12 : 6}
                                className={'d-flex flex-wrap w-100 mt-4'}
                            >
                                <Col xs={12} className={'px-4 mb-4'}>
                                    <OutlinedDropdownForm
                                        label={'Request Type'}
                                        name={`type`}
                                        required={!!required.type}
                                        data={types?.map(e => ({
                                            value: e,
                                            label: e.title,
                                            id: e.id
                                        })) ?? []}
                                    />
                                </Col>
                                <Col xs={12} className={'d-flex w-100 align-items-center mb-4'}>
                                    <Col className={'pr-4 pl-2 flex-grow-1'}>
                                        <RGAInput
                                            name={'rga'}
                                            ignore={[initialValues?.rga]}
                                            required={required.rga}
                                            value={values?.rga ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            manual={!values?.autoRga}
                                            setValidating={setValidatingRga}
                                            ref={rgaInput}
                                        />
                                    </Col>
                                    <div className={'d-flex justify-content-center pl-2'}>
                                        <CheckboxForm
                                            onChange={(e) => onAutoRgaChanged(e, form)}
                                            name={'autoRga'}
                                            required={required.autoRga}
                                            data={{
                                                value: values.autoRga, label: 'Auto RGA'
                                            }}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} className={'d-flex w-100 align-items-center mb-4 px-4'}>
                                    {
                                        values?.inventoryBased &&
                                        <OutlinedDropdownForm
                                            label={'Warehouse'}
                                            name={'warehouse'}
                                            required={required.warehouse}
                                            renderValue={v => v?.name ?? ''}
                                            data={warehouses?.map(e => ({
                                                label: e.name,
                                                value: e,
                                                id: e.id,
                                            })) ?? []}
                                        />
                                    }
                                </Col>
                            </Col>
                            <div className={'w-100 mb-5'}/>
                            <Divider className={'divider'}/>
                            <Row className='no-gutters footer justify-content-between align-items-start w-100'>
                                <Button
                                    variant={'dialog'}
                                    color={'primary'}
                                    onClick={() => setOpen(false)}
                                    borderRadius={'0 8px 0 8px'}
                                >
                                    {
                                        loadingTypes
                                            ? 'Loading'
                                            : `Close`
                                    }
                                </Button>
                                <Button
                                    disabled={submitButtonDisabled}
                                    onClick={() => form.submit()}
                                    variant={'dialog'}
                                    color={'primary'}
                                    borderRadius={'8px 0 8px 0'}
                                >
                                    {
                                        loadingTypes
                                            ? 'Loading'
                                            : haveChanges
                                                ? "Continue"
                                                : `Continue without any Changes`
                                    }
                                </Button>
                            </Row>
                        </Row>
                    );
                }}
            />
        </div>
    );

}

export default EditPartSupportRequestBasicInfo;
